import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useQuery } from "@tanstack/react-query";
import DynamicDrawer from "components/UI/Drawer/DynamicDrawer";
import DocumentUpload from "components/UI/documentUpload/DocumentUpload";
import { useDrawer } from "components/widgets/phase-3/dataHub/kpiMaster/APIs/HandleKpiMasterApi";
import ShareSurveyLinkDrawer from "components/widgets/phase-3/supplier/right/Survey/ShareSurveyLinkDrawer";
import QuestionBody from "components/widgets/phase-3/survey/surveyQuestions/QuestionComponents/right/QuestionBody";
import { useEffect, useState } from "react";
import apiBolerplate from "utils/_helpers/apiBolerplate";
import { customScrollbar, fallback } from "../riskAdmin/RiskTabs";

export const SurveySectionHeader = ({
  title = "",
  description = "",
  sx = {},
  bgcolor = "#EBF1F8",
  borderColor = "#A1BFDF",
}) => {
  return (
    <Box
      sx={{
        background: bgcolor,
        borderBottom: `1px solid ${borderColor}`,
        p: "16px",
        mb: 2,
        ...sx,
      }}
    >
      {title && (
        <Typography
          variant="h4"
          fontWeight={600}
          gutterBottom
          sx={{
            color: "#15314E",
            lineHeight: "24px",
            letterSpacing: "0.15%",
            mb: "4px",
          }}
        >
          {title}
        </Typography>
      )}

      {description && (
        <Typography
          variant="body2"
          fontWeight={400}
          sx={{
            color: "#245283",
            lineHeight: "20px",
            letterSpacing: "0.25%",
            "& ul, & ol": {
              paddingLeft: "24px",
              marginTop: "8px",
              marginBottom: "8px",
              listStylePosition: "outside",
            },
            "& li": {
              marginBottom: "4px",
              display: "list-item",
            },
            "& a": {
              wordBreak: "break-all",
              overflowWrap: "anywhere",
              whiteSpace: "normal",
              display: "inline-block",
              textAlign: "left",
            },
          }}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
    </Box>
  );
};

const useResponseStyles = makeStyles(() => ({
  questionCont: {
    border: "none",
    borderRadius: 5,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    padding: "16px 0px",
  },
  question: {
    fontSize: 14,
    fontWeight: 400,
    padding: "5px 6px",
    wordBreak: "break-all",
    border: "1px solid #E9E9E9",
    backgroundColor: "#FCFCFC",
    borderRadius: "6px",
    marginLeft: 5,
    width: "80%",
  },
  weightage: {
    fontSize: 13,
    fontWeight: 400,
    padding: "5px 6px",
    color: "#3374B9",
    border: "1px solid #EBF1F8",
    backgroundColor: "#EBF1F8",
    borderRadius: "16px",
    marginLeft: 5,
    width: "18%",
    textAlign: "center",
  },
}));

const Question = ({ question, showSubmittedAnswers = true, isFrom }) => {
  const classes = useResponseStyles();
  if (question?.questionType === "SUB_SECTION") {
    return (
      <SurveySectionHeader
        title={question.question}
        description={question.description}
      />
    );
  }
  return (
    <Box className={classes.questionCont}>
      <Box display="flex" width="100%" alignItems="center" mb={2}>
        <Typography className={classes.question}>
          {question?.question}
        </Typography>
        {question?.weight && (
          <Typography className={classes.weightage}>
            {question?.weight} Weightage
          </Typography>
        )}
      </Box>
      <Box width="100%" padding="0 10px" pointerEvents="none">
        <QuestionBody
          questionType={question?.questionType}
          answers={question?.answers}
          isFrom={
            question?.questionType === "DOCUMENT_UPLOAD" &&
            isFrom === "survey-response"
              ? "surveyResponse"
              : "publishedSurvey"
          }
          tableHeadersApi={question?.tableHeaders}
          tableRowsApi={question?.tableRows}
          questionTitle={question?.question}
          removeEditOption
          comment={question?.showComment}
          submittedAnswer={
            showSubmittedAnswers
              ? question?.tableRows?.length > 0 ||
                question?.tableHeaders?.length > 0
                ? question?.submitted_answer || []
                : question?.submitted_answer
                  ? [question?.submitted_answer]
                  : []
              : []
          }
          isPreview={true}
          calculation={{
            resolved_custom_calculation_logic:
              question?.resolved_custom_calculation_logic,
            custom_calculation_logic: question?.custom_calculation_logic,
          }}
        />
        {question?.showDocument && showSubmittedAnswers && (
          <Box style={{ marginTop: "10px" }}>
            <DocumentUpload
              value={question?.submitted_answer?.supporting_document}
              isFrom="surveyResponse"
              viewOnly={true}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const PreviewSurveyContent = ({
  surveyId,
  versionNo,
  supplier_assessment_id,
  supplierId,
  height = "100%",
  width = "100%",
  showBorder = true,
  showSubmittedAnswers = true,
  showShareButton = false,
  isFrom,
  open,
}) => {
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const { isOpen: openShareSurvey, toggleDrawer: toggleShareSurvey } =
    useDrawer();

  const { data: sectionsData, isLoading: sectionsLoading } = useQuery({
    queryKey: ["survey-sections", surveyId, versionNo],
    queryFn: async () => {
      const res = await apiBolerplate({
        url: "/survey_sections/get_all_sections",
        bodyData: {
          survey_id: surveyId,
          version: versionNo,
        },
        hideLoader: true,
        hideMsg: true,
      });
      return res;
    },
    enabled: !!surveyId,
    staleTime: Infinity,
  });

  const questionsEndpoint = showSubmittedAnswers
    ? "shared_survey/get_all_questions_for_survey_response"
    : "survey_questions/get_all_questions";

  const questionsBodyData = showSubmittedAnswers
    ? {
        section_id: selectedSection?._id,
        survey_id: surveyId,
        supplier_id: supplierId,
        supplier_assessment_id,
      }
    : {
        section_id: selectedSection?._id,
      };

  const { data: questionsData, isLoading: questionsLoading } = useQuery({
    queryKey: [
      "survey-questions-preview",
      selectedSection?._id,
      surveyId,
      supplierId,
      showSubmittedAnswers,
    ],
    queryFn: async () => {
      const res = await apiBolerplate({
        url: questionsEndpoint,
        bodyData: questionsBodyData,
        hideLoader: true,
        hideMsg: true,
      });
      return res;
    },
    enabled:
      !!selectedSection?._id &&
      (showSubmittedAnswers ? !!surveyId : true) &&
      open,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (sectionsData?.result) {
      setSections(sectionsData.result);
      if (sectionsData.result.length > 0 && !selectedSection) {
        setSelectedSection(sectionsData.result[0]);
      }
    }
  }, [sectionsData, selectedSection]);

  return (
    <>
      {/* Conditionally show Share Survey header only for template mode */}
      {isFrom !== "preview-drawer" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1,
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h3"
            sx={{ fontWeight: 600, color: "#0F0F0F", ml: 1 }}
          >
            {isFrom === "survey" ? "Survey" : "Survey Response"}
          </Typography>
          {showShareButton && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              sx={{ ml: -2 }}
              startIcon={<ShareOutlinedIcon />}
              onClick={toggleShareSurvey}
            >
              Share Survey
            </Button>
          )}
        </Box>
      )}

      <Box display="flex" flexDirection="row" width={width}>
        <Box
          sx={{
            width: "20%",
            height,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRight: "1px solid #EBF1F8",
            bgcolor: "#FBFBFB",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 1,
            }}
          >
            {sectionsLoading ? (
              fallback({ showFooter: false })
            ) : (
              <List dense disablePadding>
                {sections.map((section, index) => {
                  const isSelected = selectedSection?._id === section._id;
                  return (
                    <ListItemButton
                      key={section._id || index}
                      selected={isSelected}
                      onClick={() => setSelectedSection(section)}
                      sx={{
                        borderRadius: "8px",
                        height: 52,
                        mb: 0.5,
                        px: 2,
                        backgroundColor: isSelected ? "#EBF1F8" : "transparent",
                        "&:hover": {
                          backgroundColor: "#EBF1F8",
                        },
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: isSelected ? "#15314E" : "#6C6C6C",
                              fontWeight: 600,
                              fontSize: "14px",
                              lineHeight: "20px",
                              letterSpacing: "0.1%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {section.title || "-"}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>

        <Box
          width="80%"
          sx={{
            height,
            overflowY: "auto",
            ...customScrollbar,
          }}
        >
          {selectedSection && (
            <SurveySectionHeader
              title={selectedSection.title}
              description={selectedSection.description}
              sx={{ mb: 1 }}
            />
          )}

          {/* Questions List */}
          {questionsLoading || sectionsLoading ? (
            fallback({ showFooter: false })
          ) : (
            <Stack spacing={3}>
              {questionsData?.result?.map((question, i) => (
                <Question
                  key={question._id || i}
                  question={question}
                  showSubmittedAnswers={showSubmittedAnswers}
                  isFrom={isFrom}
                />
              ))}

              {!questionsLoading && questionsData?.result?.length === 0 && (
                <Typography>No questions available for this section</Typography>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      {/* Conditionally show Share Survey Drawer only for template mode */}
      {showShareButton && (
        <ShareSurveyLinkDrawer
          isDrawerOpen={openShareSurvey}
          handleCloseDrawer={toggleShareSurvey}
          supplierId={supplierId}
        />
      )}
    </>
  );
};

const PreviewDrawer = ({
  open,
  onClose,
  tempSelection,
  showSubmittedAnswers = true,
}) => {
  if (!tempSelection?._id && tempSelection?.version) return;
  return (
    <DynamicDrawer
      hasDrawerOpen={open}
      handleClose={onClose}
      hasDefaultActionButton={false}
      heading={
        <Typography variant="h3" sx={{ fontWeight: 600, color: "#0F0F0F" }}>
          Preview Survey
        </Typography>
      }
      sx={{ width: "75vw", p: 0 }}
      noShadowForHeading
    >
      <Box sx={{ width: "100%", height: "100%" }}>
        <PreviewSurveyContent
          surveyId={tempSelection?._id}
          versionNo={tempSelection?.version}
          showBorder={true}
          height="90vh"
          showSubmittedAnswers={!showSubmittedAnswers}
          showShareButton={!showSubmittedAnswers}
          width="100%"
          isFrom={"preview-drawer"}
        />
      </Box>
    </DynamicDrawer>
  );
};

export default PreviewDrawer;
